import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  ThemeProvider,
  createTheme,
  StepContent,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import ServiceSelection from './ServiceSelection';
import PractitionerSelection from './PractitionerSelection';
import TimeSelection from './TimeSelection';
import PatientForm from './PatientForm';
import PaymentForm from './PaymentForm';
import Confirmation from './Confirmation';
import { 
  getClinicInfo, 
  getClinicServices, 
  getAvailableTimes, 
  createBooking,
  getPractitioners 
} from '../services/bookingService';

const steps = ['Select Service', 'Select Practitioner', 'Choose Date & Time', 'Your Details', 'Confirmation'];

const BookingPage = () => {
  // Get URL parameters
  const { clinicStub } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceId = searchParams.get('service');
  const returnUrl = searchParams.get('returnUrl');
  
  // Theme setup
  const defaultTheme = useTheme();
  const [customTheme, setCustomTheme] = useState(defaultTheme);
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  
  // Check if we're on a payment completion page
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  
  // Booking data
  const [bookingData, setBookingData] = useState({
    service: null,
    practitioner: null,
    appointmentTime: null,
    patient: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },
    notes: '',
    payment: {
      status: 'pending'
    }
  });
  
  // Setup and check for payment completion
  useEffect(() => {
    console.log('BOOKING PAGE LOADED:');
    console.log('- Current URL:', window.location.href);
    console.log('- Service ID:', serviceId);
    console.log('- Return URL:', returnUrl);
    
    // Check if we're on a payment completion page
    const isComplete = window.location.href.includes('payment-complete') || 
                     window.location.href.includes('AccessCode=') ||
                     window.location.href.includes('session_id=');
    setIsPaymentComplete(isComplete);
    console.log('- Is payment complete page:', isComplete);
    
    // If we're on a payment completion page, automatically show the confirmation step
    if (isComplete) {
      setActiveStep(5); // Confirmation step
      console.log('- Setting to confirmation step automatically');
      
      // Extract payment details from URL if available
      const urlParams = new URLSearchParams(window.location.search);
      const accessCode = urlParams.get('AccessCode');
      const transactionId = urlParams.get('id') || urlParams.get('transaction_id');
      const sessionId = urlParams.get('session_id');
      const success = urlParams.get('success') === 'true';
      
      // Try to retrieve booking data from localStorage
      try {
        const bookingDataStr = localStorage.getItem('currentBooking');
        console.log('- Retrieved localStorage booking data:', bookingDataStr);
        
        if (bookingDataStr) {
          const storedBookingInfo = JSON.parse(bookingDataStr);
          
          // Set the clinicStub from localStorage if we don't have it from URL params
          if (!clinicStub && storedBookingInfo.clinicStub) {
            console.log('- Setting clinicStub from localStorage:', storedBookingInfo.clinicStub);
            // We can't directly modify clinicStub from useParams, but we can set it in state for use later
            window.history.replaceState(null, '', `/${storedBookingInfo.clinicStub}/booking/payment-complete${window.location.search}`);
            // Force reload to get the updated URL params
            window.location.reload();
            return;
          }
          
          // In payment-complete path, we need to fully restore all saved booking data
          // This ensures we don't get "Booking information is incomplete" error
          try {
            // Try to get saved booking session from sessionStorage
            const savedSession = sessionStorage.getItem('bookingSession');
            console.log('- Retrieved booking session from sessionStorage:', savedSession ? 'yes' : 'no');
            
            if (savedSession) {
              const fullBookingData = JSON.parse(savedSession);
              console.log('- Restoring full booking data from session storage');
              setBookingData(fullBookingData);
            }
          } catch (sessErr) {
            console.error('- Error retrieving from sessionStorage:', sessErr);
          }
          
          // If we have a Stripe session ID, check the payment status from the API
          if (sessionId) {
            console.log('- Found Stripe session ID in URL, checking payment status:', sessionId);
            
            // Update payment info immediately (we'll update more details after API call)
            setBookingData(prev => ({
              ...prev,
              payment: {
                ...prev.payment,
                status: 'complete',
                transactionId: sessionId
              }
            }));
            
            // Check payment status from the server
            const { checkPaymentStatus } = require('../services/bookingService');
            checkPaymentStatus(sessionId, 'stripe', storedBookingInfo.clinicId)
              .then(status => {
                console.log('- Payment status check result:', status);
                
                // If the appointment was created server-side, skip to success step
                if (status.appointment_created) {
                  console.log('- Appointment already created server-side, skipping to success step');
                  setActiveStep(6); // Skip to success step
                }
                
                // Update booking data with more details from the payment status
                setBookingData(prev => ({
                  ...prev,
                  payment: {
                    ...prev.payment,
                    status: 'complete',
                    transactionId: sessionId,
                    details: status
                  }
                }));
              })
              .catch(error => {
                console.error('- Error checking payment status:', error);
              });
          } else if (accessCode || transactionId) {
            setBookingData(prev => ({
              ...prev,
              payment: {
                ...prev.payment,
                status: 'complete',
                accessCode,
                transactionId
              }
            }));
          }
        } else {
          console.warn('- No booking data found in localStorage');
        }
      } catch (e) {
        console.error('- Error processing localStorage data:', e);
      }
    }
  }, [clinicStub]);
  
  // Load clinic data on component mount
  useEffect(() => {
    // If we're on the admin path, don't try to fetch clinic data
    if (clinicStub === 'admin') {
      console.log('Admin path detected, skipping clinic fetch');
      setLoading(false);
      return;
    }
    
    const fetchClinic = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get clinic info by stub
        const clinicResult = await getClinicInfo(null, clinicStub);

        if (!clinicResult || !clinicResult.clinic) {
          throw new Error('Clinic not found');
        }

        // Check if bookings are enabled for this clinic
        if (clinicResult.clinic.is_booking_enabled === false) {
          setError('Bookings are currently unavailable for this clinic. Please check back later or contact the clinic directly.');
          setLoading(false);
          return;
        }

        setClinic(clinicResult.clinic);
        
        // Create custom theme with clinic's primary color
        if (clinicResult.clinic.primary_color) {
          setCustomTheme(createTheme({
            ...defaultTheme,
            palette: {
              ...defaultTheme.palette,
              primary: {
                main: clinicResult.clinic.primary_color,
              },
            },
          }));
        }
        
        // Get services for this clinic
        const servicesResult = await getClinicServices(clinicResult.clinic.id);
        
        // servicesResult is already an array, not wrapped in an appointment_types property
        setServices(servicesResult || []);
        console.log('Available services:', servicesResult);
        
        // Get practitioners for this clinic
        const practitionersResult = await getPractitioners(clinicResult.clinic.id);
        setPractitioners(practitionersResult.practitioners || []);
        
        // If serviceId is provided, pre-select that service
        if (serviceId && servicesResult) {
          const selectedService = servicesResult.find(
            service => service.id.toString() === serviceId.toString()
          );
          
          if (selectedService) {
            console.log('Pre-selecting service:', selectedService.name);
            setBookingData(prev => ({
              ...prev,
              service: selectedService
            }));
            
            // If we're auto-selecting a service, advance to the next step
            if (!isPaymentComplete) {
              setActiveStep(1); // Go to practitioner selection
            }
          } else {
            console.log(`Service ID ${serviceId} not found in available services`);
          }
        }
        
      } catch (err) {
        console.error('Error loading clinic data:', err);
        setError(err.message || 'Failed to load clinic data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClinic();
  }, [clinicStub, serviceId, isPaymentComplete]);
  
  // Load available times when practitioner and service are selected
  useEffect(() => {
    // Skip fetching times if we're on the payment completion page
    if (isPaymentComplete) {
      console.log('On payment completion page - skipping available times fetch');
      return;
    }
    
    const fetchAvailableTimes = async () => {
      if (!bookingData.service || !bookingData.practitioner || !clinic) return;
      
      // If we already have a selected time, don't fetch again
      if (bookingData.appointmentTime) {
        console.log('Appointment time already selected, skipping available times fetch');
        return;
      }
      
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // Get the current date - IMPORTANT: Use actual current date, not future year
        const today = new Date();
        // Fix the year to current year, not 2025
        const currentYear = today.getFullYear();
        today.setFullYear(currentYear);
        
        console.log(`Using CURRENT date and time: ${today.toISOString()}`);
        const allAvailableTimes = [];
        
        // IMPORTANT: Cliniko API works best with requests of one day at a time
        // The API documentation states the time frame cannot exceed 7 days
        // We'll make up to 7 requests to cover a week, which is safer
        
        console.log('Fetching available times for a one-week period');
        console.log('Using service ID:', bookingData.service.id);
        console.log('Using clinic ID:', clinic.id);
        console.log('Using business ID:', clinic.business_id);
        
        // "Any Practitioner" option handling
        if (bookingData.practitioner.id === 'any') {
          console.log('Any practitioner selected - fetching times for all clinic practitioners');
          // Get the list of practitioners from the clinic
          const practitionerIds = clinic.practitioner_ids || [];
          console.log(`Found ${practitionerIds.length} practitioners for clinic`);
          
          // Track practitioners with available times
          const practitionersWithTimes = new Set();
          
          // Fetch individual practitioner data for display
          const practitionerData = {};
          for (const practitioner of practitioners) {
            practitionerData[practitioner.id] = practitioner.name;
          }
          
          // Process each day for each practitioner
          const totalDays = 7;
          for (const practitionerId of practitionerIds) {
            console.log(`Checking for times with practitioner ID: ${practitionerId}`);
            
            for (let i = 0; i < totalDays; i++) {
              try {
                // Calculate date for this window
                // TIMEZONE FIX: Create date at noon to avoid date boundary issues
                const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i, 12, 0, 0);
                
                // Format as YYYY-MM-DD using local date components
                const padZero = (num) => num < 10 ? `0${num}` : `${num}`;
                const dateStr = `${day.getFullYear()}-${padZero(day.getMonth() + 1)}-${padZero(day.getDate())}`;
                
                // Get available times for this practitioner on this day
                const dayResult = await getAvailableTimes({
                  clinic_id: clinic.id.toString(),
                  appointment_type: bookingData.service.id,
                  practitioner_id: practitionerId,
                  from: dateStr,
                  to: dateStr
                });
                
                // Process available times
                if (dayResult && dayResult.available_times && dayResult.available_times.length > 0) {
                  // Keep track of practitioners who have available times
                  practitionersWithTimes.add(practitionerId);
                  
                  // Store the practitioner info with each time slot
                  dayResult.available_times.forEach(time => {
                    // Add the actual practitioner ID that will be used for booking
                    time.original_practitioner_id = practitionerId;
                    allAvailableTimes.push(time);
                  });
                }
              } catch (dayError) {
                console.error(`Error processing day ${i+1} for practitioner ${practitionerId}:`, dayError);
              }
              
              // Add a small delay between API calls
              if (i < totalDays - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            }
          }
          
          console.log(`Found times for ${practitionersWithTimes.size} practitioners out of ${practitionerIds.length}`);
        } else {
          // Regular single practitioner flow
          console.log('Using specific practitioner ID:', bookingData.practitioner.id);
          
          // Show warning about Cliniko API limitations
          console.log('IMPORTANT CLINIKO API LIMITATIONS:');
          console.log('- The business must be enabled for online bookings');
          console.log('- The appointment type must be enabled for online bookings');
          console.log('- The practitioner must be enabled for online bookings');
        
          // Process each day window (up to 7 days)
          const totalDays = 7; // One week is safer
          let successCount = 0;
        
          for (let i = 0; i < totalDays; i++) {
            try {
              // Calculate date for this window (single day)
              // TIMEZONE FIX: Create date at noon to avoid date boundary issues
              const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i, 12, 0, 0);
              
              // Format as YYYY-MM-DD using local date components
              const padZero = (num) => num < 10 ? `0${num}` : `${num}`;
              const dateStr = `${day.getFullYear()}-${padZero(day.getMonth() + 1)}-${padZero(day.getDate())}`;
              
              console.log(`Fetching day ${i+1}/${totalDays}: ${dateStr}`);
              
              // Fetch available times for this day (use same date for from and to)
              let retry = 0;
              let dayResult = null;
              
              while (retry < 2 && dayResult === null) {
                try {
                  dayResult = await getAvailableTimes({
                    clinic_id: clinic.id.toString(),
                    appointment_type: bookingData.service.id,
                    practitioner_id: bookingData.practitioner.id,
                    from: dateStr,
                    to: dateStr // Same day for from and to
                  });
                  
                  // Add times from this day to our full result set
                  // Note: dayResult.available_times should always exist but might be empty
                  if (dayResult && dayResult.available_times) {
                    const count = dayResult.available_times.length;
                    
                    if (count > 0) {
                      console.log(`Day ${i+1}: Found ${count} available times`);
                      
                      // Add practitioner info to each time slot
                      const timesWithPractitioner = dayResult.available_times.map(time => ({
                        ...time,
                        original_practitioner_id: bookingData.practitioner.id
                      }));
                      
                      allAvailableTimes.push(...timesWithPractitioner);
                    } else {
                      console.log(`Day ${i+1}: No available times found (empty array)`);
                    }
                    
                    // Count as success even if no times found - at least the API call worked
                    successCount++;
                  } else {
                    console.warn(`Day ${i+1}: Unexpected response format`);
                    console.warn('Response structure:', dayResult ? Object.keys(dayResult) : 'null');
                  }
                } catch (retryError) {
                  console.error(`Day ${i+1}, Attempt ${retry + 1} failed:`, retryError);
                  retry++;
                  if (retry >= 2) {
                    console.error(`Day ${i+1}: Failed after multiple attempts`);
                  } else {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                  }
                }
              }
              
              // Break early if we've had several successful calls with no available times
              // This is to optimize and not make unnecessary calls if we're not finding any times
              if (successCount >= 3 && allAvailableTimes.length === 0) {
                console.log('Several successful calls with no times found, stopping early');
                break;
              }
              
              // Add a small delay between API calls to avoid rate limiting
              if (i < totalDays - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (dayError) {
              console.error(`Error processing day ${i+1}:`, dayError);
              // Continue to next day even if this one fails
            }
          }
        }
        
        // Create a result object similar to what the API would return
        const timesResult = { available_times: allAvailableTimes };
        console.log(`Total available times found across all windows: ${allAvailableTimes.length}`);
        
        if (!timesResult || !timesResult.available_times) {
          console.warn('No available times found or invalid response format');
          setAvailableTimes([]);
        } else {
          // Sort available times by start time
          const sortedTimes = timesResult.available_times.sort((a, b) => {
            return new Date(a.iso) - new Date(b.iso);
          });
          
          console.log(`Found ${sortedTimes.length} available times`);
          setAvailableTimes(sortedTimes);
        }
      } catch (err) {
        console.error('Error loading available times:', err);
        setError('Unable to load available appointment times. Please try again later or contact the clinic directly.');
        setAvailableTimes([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (bookingData.service && bookingData.practitioner && !isPaymentComplete) {
      fetchAvailableTimes();
    }
  }, [bookingData.service, bookingData.practitioner, clinic, practitioners, isPaymentComplete, bookingData.appointmentTime]);
  
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handleServiceSelect = async (service) => {
    setBookingData({
      ...bookingData,
      service,
      practitioner: null, // Reset practitioner when service changes
      appointmentTime: null // Reset time selection when service changes
    });
    
    // Fetch practitioners who can provide this service
    if (clinic && service) {
      try {
        setLoading(true);
        console.log(`Fetching practitioners for service ID: ${service.id}`);
        
        // Get practitioners for this service
        const practitionersResult = await getPractitioners(clinic.id, service.id);
        setPractitioners(practitionersResult.practitioners || []);
        console.log(`Filtered practitioners for service ${service.name}: ${practitionersResult.practitioners?.length || 0}`);
      } catch (error) {
        console.error('Error loading practitioners for service:', error);
        setError('Failed to load practitioners for this service');
      } finally {
        setLoading(false);
      }
    }
    
    handleNext();
  };
  
  const handlePractitionerSelect = (practitioner) => {
    setBookingData({
      ...bookingData,
      practitioner,
      appointmentTime: null // Reset time selection when practitioner changes
    });
    handleNext();
  };
  
  const handleTimeSelect = (time) => {
    // SIMPLE FIX: Save the exact time display that the user selected
    if (time.time_display) {
      sessionStorage.setItem('user_selected_time_display', time.time_display);
      console.log('Saved user selected time:', time.time_display);
    }
    
    // If this is from the "Any Practitioner" option, we need to use the original practitioner ID
    // for the actual appointment booking
    if (bookingData.practitioner.id === 'any' && time.original_practitioner_id) {
      // Find the actual practitioner in our list
      const actualPractitioner = practitioners.find(p => p.id === time.original_practitioner_id);
      
      // Use the original practitioner for the appointment
      setBookingData({
        ...bookingData,
        practitioner: actualPractitioner || bookingData.practitioner,
        appointmentTime: time
      });
    } else {
      setBookingData({
        ...bookingData,
        appointmentTime: time
      });
    }
    handleNext();
  };
  
  const handlePatientInfoSubmit = (patientData, notes) => {
    setBookingData({
      ...bookingData,
      patient: patientData,
      notes
    });
    handleNext();
  };
  
  const handlePaymentSuccess = (paymentData) => {
    setBookingData({
      ...bookingData,
      payment: {
        status: 'complete',
        transactionId: paymentData.transactionId,
        accessCode: paymentData.accessCode
      }
    });
    handleNext();
  };
  
  const handlePaymentCancel = () => {
    handleBack();
  };
  
  const handleConfirmBooking = async () => {
    // Check if this is a payment-complete page, with URL evidence of payment
    const isPaymentCompletePage = window.location.pathname.includes('payment-complete');
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionId = urlParams.get('session_id');
    const hasSuccess = urlParams.get('success') === 'true';
    
    if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
      setError('Booking information is incomplete');
      return;
    }
    
    // On payment-complete path, we can skip the payment status check
    const paymentEvidenceInURL = (isPaymentCompletePage || hasSessionId || hasSuccess);
    const paymentStatus = bookingData.payment && bookingData.payment.status === 'complete';
    
    // Check if payment is complete, unless we're on payment-complete path
    if (!paymentStatus && !paymentEvidenceInURL) {
      setError('Payment must be completed before confirming booking');
      return;
    }
    
    // Check for existing pending request - prevent rapid duplicate submissions
    const pendingRequestTime = sessionStorage.getItem('pending_booking_request_time');
    if (pendingRequestTime) {
      const lastAttemptTime = parseInt(pendingRequestTime, 10);
      const now = Date.now();
      const timeSinceLastAttempt = now - lastAttemptTime;
      
      // If there's an existing request less than 3 seconds old, block this one
      if (timeSinceLastAttempt < 3000) {
        setError('Please wait a moment before trying again.');
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Calculate end time based on service duration - we need to calculate this since we only have the start time
      const startTime = new Date(bookingData.appointmentTime.iso);
      const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
      
      // Get transaction ID from either booking data or URL
      const transactionId = 
        (bookingData.payment && bookingData.payment.transactionId) || 
        urlParams.get('session_id') || 
        urlParams.get('id') || 
        urlParams.get('transaction_id') || 
        'payment-completed';
      
      // Include payment information in notes
      const paymentNotes = `Payment completed: Transaction ID ${transactionId}`;
      const combinedNotes = bookingData.notes 
        ? `${bookingData.notes}\n\n${paymentNotes}` 
        : paymentNotes;
      
      console.log('Creating booking with payment evidence:', {
        isPaymentCompletePage,
        hasSessionId,
        transactionId,
        paymentStatus,
      });
      
      const bookingResult = await createBooking({
        clinic_id: clinic.id.toString(),
        appointment_type_id: bookingData.service.id,
        practitioner_id: bookingData.practitioner.id,
        starts_at: startTime.toISOString(),
        ends_at: endTime.toISOString(),
        patient_first_name: bookingData.patient.first_name,
        patient_last_name: bookingData.patient.last_name,
        patient_email: bookingData.patient.email,
        patient_phone: bookingData.patient.phone,
        notes: combinedNotes
      });
      
      // Success! Booking is confirmed
      setActiveStep(6); // Move to success step (beyond confirmation)
      
      // Clean up storage after successful booking
      try {
        sessionStorage.removeItem('bookingSession');
        localStorage.removeItem('currentBooking');
        sessionStorage.removeItem('pending_booking_request_time');
        console.log('Cleared booking data from storage after successful booking');
      } catch (e) {
        console.error('Error clearing storage:', e);
      }
      
    } catch (err) {
      console.error('Error creating booking:', err);
      
      // Handle specific error types with user-friendly messages
      if (err.response) {
        // Handle 409 Conflict errors specifically
        if (err.response.status === 409) {
          // Check if this was a duplicate request
          if (err.response.data && err.response.data.code === 'DUPLICATE_REQUEST') {
            // This might actually be a successful booking that's being resubmitted
            console.log('This appears to be a duplicate request - checking if booking was already successful');
            
            // Set a flag in sessionStorage to indicate we've seen a duplicate request
            sessionStorage.setItem('possible_duplicate_booking', 'true');
            
            // Try to retrieve the pending booking request time
            const pendingTime = sessionStorage.getItem('pending_booking_request_time');
            if (pendingTime) {
              const attemptTime = new Date(parseInt(pendingTime, 10));
              console.log(`Original request was at: ${attemptTime.toISOString()}`);
            }
            
            // Check if we need to proceed to success page
            const duplicateSuccessData = sessionStorage.getItem('duplicate_booking_success');
            if (duplicateSuccessData === 'true') {
              console.log('Previous duplicate was successful - proceeding to success page');
              setActiveStep(6); // Move to success step
              return;
            }
            
            // Show a more specific error for duplicates
            setError('This booking may have already been processed. Please check your email for confirmation before trying again.');
          } else if (err.response.data && err.response.data.code === 'TIME_CONFLICT') {
            // Time slot is no longer available
            setError('This time slot is no longer available. Please select another time.');
            // Take them back to time selection
            setActiveStep(2);
          } else {
            // Generic conflict error
            setError('There was a conflict with your booking. Please try again or select a different time.');
          }
        } else {
          // Handle other HTTP errors
          setError(err.response.data?.message || 'Failed to create booking. Please try again.');
        }
      } else {
        // For non-HTTP errors
        setError(err.message || 'Failed to create booking');
      }
    } finally {
      setLoading(false);
      
      // Clear pending request flag after a delay
      setTimeout(() => {
        sessionStorage.removeItem('pending_booking_request_time');
      }, 5000);
    }
  };
  
  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Service selection
        return !bookingData.service;
      case 1: // Practitioner selection
        return !bookingData.practitioner;
      case 2: // Time selection
        return !bookingData.appointmentTime;
      case 3: // Patient info
        return !bookingData.patient.first_name || 
               !bookingData.patient.last_name || 
               !bookingData.patient.email;
      case 4: // Payment
        // Payment step doesn't use the next button - it's handled within the payment component
        return true;
      case 5: // Confirmation
        return !bookingData.payment || bookingData.payment.status !== 'complete';
      default:
        return false;
    }
  };
  
  // Render content based on current step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ServiceSelection 
            services={services} 
            selectedService={bookingData.service}
            onServiceSelect={handleServiceSelect}
            loading={loading}
          />
        );
      case 1:
        return (
          <PractitionerSelection 
            practitioners={practitioners} 
            selectedPractitioner={bookingData.practitioner}
            onPractitionerSelect={handlePractitionerSelect}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 2:
        return (
          <TimeSelection 
            availableTimes={availableTimes} 
            selectedTime={bookingData.appointmentTime}
            onTimeSelect={handleTimeSelect}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 3:
        return (
          <PatientForm 
            patientInfo={bookingData.patient}
            notes={bookingData.notes} 
            onSubmit={handlePatientInfoSubmit}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 4:
        if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
          return <Alert severity="error">Missing clinic, service, practitioner, or appointment time information</Alert>;
        }
        
        // Calculate appointment start and end times
        const startTime = new Date(bookingData.appointmentTime.iso);
        const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
        
        // Include payment information in notes
        const bookingNotes = bookingData.notes || '';
        
        // Expose booking data for session storage
        window.bookingData = bookingData;
        
        return (
          <PaymentForm 
            clinicId={clinic.id.toString()}
            serviceId={bookingData.service.id}
            practitionerId={bookingData.practitioner.id}
            amount={bookingData.service.price !== null ? Number(bookingData.service.price) : 0}
            patientEmail={bookingData.patient.email}
            patientName={`${bookingData.patient.first_name} ${bookingData.patient.last_name}`}
            // Pass all appointment details for potential automatic creation
            appointmentDetails={{
              starts_at: startTime.toISOString(),
              ends_at: endTime.toISOString(),
              patient_first_name: bookingData.patient.first_name,
              patient_last_name: bookingData.patient.last_name,
              patient_phone: bookingData.patient.phone,
              notes: bookingNotes
            }}
            returnUrl={returnUrl}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
            loading={loading}
          />
        );
      case 5:
        return (
          <Confirmation 
            bookingData={bookingData}
            clinic={clinic}
            onConfirm={handleConfirmBooking}
            loading={loading}
          />
        );
      case 6:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" gutterBottom sx={{backgroundColor: 'primary.main', padding: '10px', color: 'white'}}>
              🎉 Appointment Confirmed! 🎉
            </Typography>
            <Typography>
              Your appointment has been booked successfully. You should receive a confirmation email shortly.
            </Typography>
            {/* Use the stored time from sessionStorage if available */}
            {(() => {
              // Try to get the stored appointment time from sessionStorage
              let displayTime = '';
              try {
                displayTime = sessionStorage.getItem('appointment_time_display') || '';
                console.log('Retrieved appointment time from sessionStorage:', displayTime);
                console.log('displayTime truthiness:', !!displayTime);
                console.log('displayTime length:', displayTime.length);
              } catch (err) {
                console.error('Error retrieving stored time:', err);
              }
              
              return (
                <Box sx={{ mt: 3 }}>
                  {bookingData.service && bookingData.practitioner && (
                    <Typography variant="subtitle1">
                      {bookingData.service.name} with {bookingData.practitioner.name}
                    </Typography>
                  )}
                  <Typography className="appointment-time time-display">
                    {/* Use sessionStorage time first, fall back to formatted time */}
                    {displayTime || 
                      (() => {
                        console.log('Falling back to formatting from ISO - displayTime was:', displayTime);
                        if (bookingData.appointmentTime && bookingData.appointmentTime.iso) {
                          // Format the time in Melbourne timezone to ensure consistency
                          const timeObj = new Date(bookingData.appointmentTime.iso);
                          console.log('Formatting ISO:', bookingData.appointmentTime.iso);
                          const formatted = new Intl.DateTimeFormat('en-AU', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Australia/Melbourne'
                          }).format(timeObj);
                          console.log('Formatted result:', formatted);
                          return formatted;
                        }
                        return bookingData.appointmentTime?.formatted_melbourne || 
                               bookingData.appointmentTime?.formatted || 
                               'Time not available';
                      })()}
                  </Typography>
                  {bookingData.payment && bookingData.payment.transactionId && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Payment Transaction ID: {bookingData.payment.transactionId}
                    </Typography>
                  )}
                </Box>
              );
            })()}
            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained"
                sx={{fontSize: '18px', padding: '15px'}}
                color="primary"
                onClick={() => {
                  console.log('Return button clicked');
                  
                  // Check for returnUrl in URL parameters
                  if (returnUrl) {
                    try {
                      const decodedUrl = decodeURIComponent(returnUrl);
                      console.log('Returning to provided URL:', decodedUrl);
                      window.location.href = decodedUrl;
                      return;
                    } catch (e) {
                      console.error('Error decoding returnUrl:', e);
                    }
                  }
                  
                  // If no returnUrl or failed to decode, try to go back to the clinic home page
                  window.location.href = `/${clinicStub}`;
                }}
              >
                Return to Home
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };
  
  // Mobile-optimized stepper for smaller screens
  const renderMobileStepper = () => (
    <Stepper activeStep={activeStep} orientation="vertical">
      {steps.map((label, index) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
          <StepContent sx={{ 
            // For confirmation step, ensure no height constraints
            ...(index === 4 && { 
              minHeight: 'auto',
              overflow: 'visible',
              pb: 12 // Extra padding for fixed button
            })
          }}>
            {renderStepContent(index)}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
  
  // Desktop stepper for larger screens
  const renderDesktopStepper = () => (
    <>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent(activeStep)}
    </>
  );
  
  const handleBackButtonClick = () => {
    // Go back to the referring page if there is one
    if (document.referrer) {
      window.location.href = document.referrer;
    } else {
      // If no referrer, try to go back in history
      navigate(-1);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back button - only show on first step or on success page */}
        {(activeStep === 0 || activeStep === 6) && (
          <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
            <IconButton 
              aria-label="back"
              onClick={handleBackButtonClick}
              sx={{ 
                bgcolor: 'rgba(0, 0, 0, 0.05)', 
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' } 
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        )}
        
        {loading && activeStep === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error && activeStep !== 6 ? (
          <Box>
            {/* Clinic header even when there's an error */}
            {clinic && (
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                {/* Logo display - single logo centered, dual logos side by side */}
                {(clinic?.logo_url || clinic?.logo_url_secondary) && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
                    {clinic?.logo_url && (
                      <img
                        src={clinic.logo_url}
                        alt={`${clinic.name} primary logo`}
                        style={{ maxHeight: 60, maxWidth: clinic?.logo_url_secondary ? '45%' : '100%' }}
                      />
                    )}
                    {clinic?.logo_url_secondary && (
                      <img
                        src={clinic.logo_url_secondary}
                        alt={`${clinic.name} secondary logo`}
                        style={{ maxHeight: 60, maxWidth: clinic?.logo_url ? '45%' : '100%' }}
                      />
                    )}
                  </Box>
                )}
                <Typography variant="h4" component="h1" gutterBottom>
                  {clinic?.name || 'Medical Appointment Booking'}
                </Typography>
              </Box>
            )}
            <Alert
              severity={error.includes('unavailable') ? 'warning' : 'error'}
              sx={{
                mb: 4,
                py: 3,
                '& .MuiAlert-message': {
                  fontSize: '1.1rem',
                  textAlign: 'center',
                  width: '100%'
                }
              }}
              icon={error.includes('unavailable') ? <BlockIcon fontSize="large" /> : undefined}
            >
              {error}
            </Alert>
          </Box>
        ) : (
          <>
            {/* Clinic header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              {/* Logo display - single logo centered, dual logos side by side */}
              {(clinic?.logo_url || clinic?.logo_url_secondary) && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
                  {clinic?.logo_url && (
                    <img 
                      src={clinic.logo_url} 
                      alt={`${clinic.name} primary logo`} 
                      style={{ maxHeight: 60, maxWidth: clinic?.logo_url_secondary ? '45%' : '100%' }} 
                    />
                  )}
                  {clinic?.logo_url_secondary && (
                    <img 
                      src={clinic.logo_url_secondary} 
                      alt={`${clinic.name} secondary logo`} 
                      style={{ maxHeight: 60, maxWidth: clinic?.logo_url ? '45%' : '100%' }} 
                    />
                  )}
                </Box>
              )}
              <Typography variant="h4" component="h1" gutterBottom>
                {clinic?.name || 'Medical Appointment Booking'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Book your appointment online
              </Typography>
            </Box>
            
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
              }}
            >
              {/* Show different stepper based on screen size */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {renderMobileStepper()}
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {renderDesktopStepper()}
              </Box>
            </Paper>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default BookingPage;